import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Loader2, Mail, Plus, UserCog, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { useTranslation } from "../../i18n";
import {
  fetchEmailPreferences,
  saveEmailPreference,
  type EmailPreference,
  type EmailPreferencesResponse,
} from "../../services/alertPreferences";

/**
 * Email alert preferences — rendered on the Alerts page.
 *
 * Three toggles per site, each mapping to a column on alert_recipients:
 *   Overdue alerts     immediate digest when checkpoints go past their interval
 *   Daily digest       once-a-day compliance roll-up
 *   Officer summary    scheduled recorded-activity summary (strictly opt-in;
 *                      defaults off, never falls back to a broadcast)
 *
 * Admins/directors get an override section per site to view everyone's
 * preferences, flip them, and enroll someone who never logs in. Every change
 * is stamped updated_by server-side.
 */

type ToggleKey = "notify_overdue" | "notify_daily" | "notify_officer_summary";

const TOGGLES: Array<{ key: ToggleKey; labelKey: string; fallback: string; hintKey: string; hint: string }> = [
  {
    key: "notify_overdue",
    labelKey: "emailPrefs.overdue",
    fallback: "Overdue alerts",
    hintKey: "emailPrefs.overdueHint",
    hint: "Immediate email when checkpoints pass their scan interval",
  },
  {
    key: "notify_daily",
    labelKey: "emailPrefs.daily",
    fallback: "Daily digest",
    hintKey: "emailPrefs.dailyHint",
    hint: "One compliance summary per day",
  },
  {
    key: "notify_officer_summary",
    labelKey: "emailPrefs.officerSummary",
    fallback: "Officer activity summary",
    hintKey: "emailPrefs.officerSummaryHint",
    hint: "Weekly recorded-activity report. Off unless you opt in.",
  },
];

interface SwitchProps {
  checked: boolean;
  disabled?: boolean;
  // Core no-unused-vars false-positives on function-type param names under the
  // TS parser (HospitalSelector.tsx trips the same); the real fix is switching
  // the config to @typescript-eslint/no-unused-vars.
  // eslint-disable-next-line no-unused-vars
  onChange: (next: boolean) => void;
  label: string;
}

function Switch({ checked, disabled, onChange, label }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-md transition-colors",
        checked ? "bg-blue-600" : "bg-gray-300",
        disabled && "opacity-50"
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-sm bg-white shadow transition-transform",
          checked ? "translate-x-6" : "translate-x-1"
        )}
      />
    </button>
  );
}

export const EmailPreferences: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<EmailPreferencesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null); // hospital_id|key being saved
  const [expanded, setExpanded] = useState(false);
  const [adminSite, setAdminSite] = useState<string | null>(null); // hospital_id with admin panel open
  const [enrollEmail, setEnrollEmail] = useState("");
  const [enrollName, setEnrollName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Admins always fetch the full recipient list so the override panel is
      // ready; the API ignores all=1 for non-admins.
      const result = await fetchEmailPreferences(true);
      setData(result);
      setError(null);
    } catch (err: any) {
      setError(err?.message || t("emailPrefs.loadFailed", "Could not load email preferences"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const myPrefByHospital = useMemo(() => {
    const m = new Map<string, EmailPreference>();
    for (const p of data?.preferences ?? []) m.set(p.hospital_id, p);
    return m;
  }, [data]);

  const othersByHospital = useMemo(() => {
    const m = new Map<string, EmailPreference[]>();
    for (const p of data?.all_recipients ?? []) {
      if (p.email.toLowerCase() === data?.email) continue;
      const list = m.get(p.hospital_id) ?? [];
      list.push(p);
      m.set(p.hospital_id, list);
    }
    return m;
  }, [data]);

  const save = async (input: Parameters<typeof saveEmailPreference>[0], busyKey: string) => {
    setSaving(busyKey);
    try {
      await saveEmailPreference(input);
      setError(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("emailPrefs.saveFailed", "Could not save preference"));
    } finally {
      setSaving(null);
    }
  };

  const toggleSelf = (hospitalId: string, key: ToggleKey, value: boolean) => {
    const existing = myPrefByHospital.get(hospitalId);
    void save(
      {
        hospital_id: hospitalId,
        // First save at a site: only enable what was clicked. The other two
        // immediate-alert defaults stay server-side (overdue/daily on) which
        // matches what the API would do for a bare row.
        [key]: value,
        active: value || existing
          ? existing
            ? undefined
            : true
          : undefined,
      },
      `${hospitalId}|${key}`
    );
  };

  const toggleOther = (p: EmailPreference, key: ToggleKey, value: boolean) => {
    void save(
      { hospital_id: p.hospital_id, email: p.email, [key]: value },
      `${p.hospital_id}|${p.email}|${key}`
    );
  };

  const enroll = (hospitalId: string) => {
    if (!enrollEmail.trim()) return;
    void save(
      {
        hospital_id: hospitalId,
        email: enrollEmail.trim(),
        name: enrollName.trim() || undefined,
        notify_overdue: true,
        notify_daily: true,
        active: true,
      },
      `${hospitalId}|enroll`
    );
    setEnrollEmail("");
    setEnrollName("");
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-blue-600" />
          <div>
            <h2 className="font-semibold text-gray-800">
              {t("emailPrefs.title", "Email Alert Preferences")}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {t(
                "emailPrefs.subtitle",
                "Choose which compliance emails you receive, per site."
              )}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-gray-200 px-6 py-4">
          {error && (
            <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <span>{error}</span>
              <button onClick={() => setError(null)} aria-label={t("common.close", "Close")}>
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {loading || !data ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : data.hospitals.length === 0 ? (
            <p className="py-4 text-sm text-gray-500">
              {t("emailPrefs.noSites", "No sites in your access list.")}
            </p>
          ) : (
            <div className="space-y-4">
              <p className="text-xs leading-relaxed text-gray-500">
                {t(
                  "emailPrefs.explainer",
                  "Sites where nobody has saved a preference fall back to emailing all supervisors and directors. Saving any preference at a site switches it to explicit choices only."
                )}
              </p>

              {data.hospitals.map((h) => {
                const mine = myPrefByHospital.get(h.id);
                const others = othersByHospital.get(h.id) ?? [];
                const adminOpen = adminSite === h.id;

                return (
                  <div key={h.id} className="rounded-lg border border-gray-200">
                    <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-medium text-gray-800">{h.name}</div>
                        {!mine && (
                          <div className="text-xs text-gray-400">
                            {t("emailPrefs.noPreference", "No preference saved — fallback applies")}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        {TOGGLES.map(({ key, labelKey, fallback }) => {
                          const busy = saving === `${h.id}|${key}`;
                          const checked = mine
                            ? mine.active && mine[key]
                            : false;
                          return (
                            <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                              {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                              ) : (
                                <Switch
                                  checked={checked}
                                  onChange={(v) => toggleSelf(h.id, key, v)}
                                  label={`${t(labelKey, fallback)} — ${h.name}`}
                                />
                              )}
                              {t(labelKey, fallback)}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {data.is_admin && (
                      <div className="border-t border-gray-100 px-4 py-2">
                        <button
                          onClick={() => setAdminSite(adminOpen ? null : h.id)}
                          className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-blue-700"
                        >
                          <UserCog className="h-3.5 w-3.5" />
                          {t("emailPrefs.manageOthers", "Manage recipients at this site")}
                          {others.length > 0 && ` (${others.length})`}
                          {adminOpen ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                        </button>

                        {adminOpen && (
                          <div className="mt-2 space-y-2 pb-2">
                            {others.length === 0 && (
                              <p className="text-xs text-gray-400">
                                {t("emailPrefs.noOthers", "No other recipients configured here.")}
                              </p>
                            )}
                            {others.map((p) => (
                              <div
                                key={p.id}
                                className="flex flex-col gap-2 rounded-md bg-gray-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="min-w-0">
                                  <div className="truncate text-sm text-gray-800">
                                    {p.name || p.email}
                                  </div>
                                  {p.name && (
                                    <div className="truncate text-xs text-gray-500">{p.email}</div>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                  {TOGGLES.map(({ key, labelKey, fallback }) => {
                                    const busy = saving === `${p.hospital_id}|${p.email}|${key}`;
                                    return (
                                      <label
                                        key={key}
                                        className="flex items-center gap-1.5 text-xs text-gray-600"
                                      >
                                        {busy ? (
                                          <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
                                        ) : (
                                          <Switch
                                            checked={p.active && p[key]}
                                            onChange={(v) => toggleOther(p, key, v)}
                                            label={`${t(labelKey, fallback)} — ${p.email}`}
                                          />
                                        )}
                                        {t(labelKey, fallback)}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}

                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                              <input
                                type="email"
                                value={enrollEmail}
                                onChange={(e) => setEnrollEmail(e.target.value)}
                                placeholder={t("emailPrefs.enrollEmail", "email@emoryhealthcare.org")}
                                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                              />
                              <input
                                value={enrollName}
                                onChange={(e) => setEnrollName(e.target.value)}
                                placeholder={t("emailPrefs.enrollName", "Name (optional)")}
                                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                              />
                              <button
                                onClick={() => enroll(h.id)}
                                disabled={!enrollEmail.trim() || saving === `${h.id}|enroll`}
                                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                <Plus className="h-4 w-4" />
                                {t("emailPrefs.enroll", "Add recipient")}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              <p className="text-xs leading-relaxed text-gray-400">
                {t(
                  "emailPrefs.officerSummaryNote",
                  "The officer activity summary is a scheduled report of recorded scan activity with its caveats included — it is not a performance score, and it is never sent to anyone who has not opted in."
                )}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
